import type {
  PatientChatUploadedFile,
  PatientChatUploadedFileSourceType,
} from "../../../lib/patientChat";

export const ATTACHMENT_LIMIT = 5;
export const MAX_TOTAL_ATTACHMENT_BYTES = 300 * 1024 * 1024;
export const MAX_CSV_BYTES = 100 * 1024 * 1024;
export const ACCEPTED_FILE_TYPES = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.md,.json";

const TEXT_MIME_TYPES = new Set(["text/plain", "text/csv", "text/markdown", "application/json"]);
const IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
]);
const TEXT_EXTENSION_MIME_TYPES: Record<string, string> = {
  txt: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
  json: "application/json",
};

interface AttachmentDescriptor {
  sourceType: PatientChatUploadedFileSourceType;
  mimeType: string;
}

export const formatAttachmentSize = (sizeBytes: number): string => {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "0 B";
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf(".");
  return lastDotIndex >= 0 ? filename.slice(lastDotIndex + 1).toLowerCase() : "";
};

export const getAttachmentDescriptor = (file: File): AttachmentDescriptor | null => {
  const extension = getFileExtension(file.name || "");
  const mimeType = (file.type || "").toLowerCase();

  if (mimeType === "application/pdf" || extension === "pdf") {
    return { sourceType: "pdf", mimeType: "application/pdf" };
  }
  if (IMAGE_MIME_TYPES.has(mimeType)) return { sourceType: "image", mimeType };
  if (TEXT_MIME_TYPES.has(mimeType)) return { sourceType: "text", mimeType };
  const textMimeType = TEXT_EXTENSION_MIME_TYPES[extension];
  return textMimeType ? { sourceType: "text", mimeType: textMimeType } : null;
};

const toMegabytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

interface SizedFile {
  name: string;
  size: number;
  type?: string;
}

const isCsv = ({ name, type }: SizedFile) =>
  (type || "").toLowerCase() === "text/csv" || getFileExtension(name || "") === "csv";

/** Returns a user-facing message when the files are too large, or "" when they fit. */
export const getAttachmentSizeError = (
  existingFiles: PatientChatUploadedFile[],
  selectedFiles: File[]
): string => {
  const oversizedCsv = selectedFiles.find((file) => isCsv(file) && file.size > MAX_CSV_BYTES);
  if (oversizedCsv) {
    return `CSV files can be up to ${toMegabytes(MAX_CSV_BYTES)}MB. ${oversizedCsv.name} is ${formatAttachmentSize(oversizedCsv.size)}.`;
  }

  const totalBytes =
    existingFiles.reduce((total, file) => total + (file.sizeBytes || 0), 0) +
    selectedFiles.reduce((total, file) => total + file.size, 0);
  if (totalBytes <= MAX_TOTAL_ATTACHMENT_BYTES) return "";

  const totalLimit = `${toMegabytes(MAX_TOTAL_ATTACHMENT_BYTES)}MB`;
  const hasCsv =
    selectedFiles.some(isCsv) ||
    existingFiles.some((file) =>
      isCsv({ name: file.filename, size: file.sizeBytes, type: file.mimeType })
    );
  return hasCsv
    ? `CSV files must be ${toMegabytes(MAX_CSV_BYTES)}MB or less and can't be combined with other files past ${totalLimit} total. Upload the CSV on its own.`
    : `Uploaded files exceed the ${totalLimit} total limit for Smart Assist summaries.`;
};

const readFile = (file: File, method: "readAsText" | "readAsDataURL") =>
  new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}.`));
    reader[method](file);
  });

export const buildUploadedFile = async (file: File): Promise<PatientChatUploadedFile> => {
  const descriptor = getAttachmentDescriptor(file);
  if (!descriptor) {
    throw new Error(
      `${file.name} is not a supported file type. Upload PDF, image, TXT, CSV, MD, or JSON files.`
    );
  }

  if (descriptor.sourceType === "text") {
    const textContent = await readFile(file, "readAsText");
    return {
      filename: file.name,
      mimeType: descriptor.mimeType,
      sourceType: descriptor.sourceType,
      sizeBytes: file.size,
      textContent: typeof textContent === "string" ? textContent : String(textContent || ""),
    };
  }

  const dataUrl = await readFile(file, "readAsDataURL");
  const base64Data =
    typeof dataUrl === "string" && dataUrl.includes(",") ? dataUrl.split(",")[1] : "";
  if (!base64Data) throw new Error(`Failed to prepare ${file.name} for upload.`);

  return {
    filename: file.name,
    mimeType: descriptor.mimeType,
    sourceType: descriptor.sourceType,
    sizeBytes: file.size,
    base64Data,
  };
};

/** Saved sessions keep file metadata but not payloads, so restored files can't be resent. */
export const hasUploadPayload = (file: PatientChatUploadedFile | null | undefined): boolean =>
  Boolean(file?.base64Data || file?.textContent);

export const getUploadedFileKey = (file: PatientChatUploadedFile): string =>
  `${file.filename}:${file.sizeBytes}:${file.mimeType}`;
